import React, {useState, useEffect, useRef} from "react"
import {connect} from "react-redux";
import classNames from "classnames"
import {getProductIdentifierFrom, getDefaultProductImageURLBy} from "../../helpers/productHelper";
import {ProductImageStacType} from "../../types/product";
import {canQuicklookPreviewBeLoaded, findQuicklookPreviewFromProductImage} from "../../helpers/quicklookPreviewHelper";
import {QuicklookPreviewType} from "../../types/geoMap";
import {getQuicklookPreviewDispatchAction} from "../../state/geoMap/dispatchActions";

import loadingImageUrl from "../../images/loading.gif";
import useIntersectionObserver from "../../hooks/use-intersection-observer";
import {AllQuicklookPreviewsType} from "../../state/geoMap/types";
import {getSearchFormValuesByState} from "../../helpers/configurationHelper";
import {ConfigurationsType} from "../../types/configurations";

interface WrapperPropsType {
  configurations: ConfigurationsType,
  productImage: ProductImageStacType,
  allQuicklookPreviews: AllQuicklookPreviewsType,
  className?: string,
  searchFormValues: any,
  getQuicklookPreviewActionHandler: any
}

const QuicklookPreviewWrapper = (props: WrapperPropsType) => {
  const {
    searchFormValues,
    ...quicklookPreviewProps
  } = props;

  const {
    productImage,
    className
  } = quicklookPreviewProps;

  const productImageId = getProductIdentifierFrom(productImage);
  return canQuicklookPreviewBeLoaded(searchFormValues)
    ? <QuicklookPreviewContainer {...quicklookPreviewProps} />
    : (
      <img
        key={`quicklook-preview-${productImageId}-default-cannot-be-loaded`}
        className={classNames("quicklook-preview", className)}
        src={getDefaultProductImageURLBy(productImage)}
        alt={`Quicklook Preview ${productImageId}`}
      />
    )
}

interface PropsType {
  configurations: ConfigurationsType,
  productImage: ProductImageStacType,
  allQuicklookPreviews: AllQuicklookPreviewsType,
  className?: string,
  getQuicklookPreviewActionHandler: any
}

const QuicklookPreviewContainer = (props: PropsType) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useIntersectionObserver({
    target: ref,
    onIntersect: ([{isIntersecting}], observerElement) => {
      if (isIntersecting) {
        setIsVisible(true);
        observerElement.unobserve(ref.current);
      }
    }
  });

  const productImageId = getProductIdentifierFrom(props.productImage);
  return (
    <div ref={ref} key={`quicklook-preview-${productImageId}-container`}>
      {isVisible && <QuicklookPreview {...props} />}
    </div>
  );
};

const QuicklookPreview = (props: PropsType) => {
  const {
    configurations,
    productImage,
    allQuicklookPreviews,
    className,
    getQuicklookPreviewActionHandler
  } = props;

  const quicklookPreview = findQuicklookPreviewFromProductImage(allQuicklookPreviews, productImage);

  const [productImageURL, setProductImageURL] = useState(
    // can be null if the preview has not been downloaded yet
    // can be the url of image if the preview has been downloaded
    (!!quicklookPreview && (quicklookPreview.url || getDefaultProductImageURLBy(productImage)))
  );

  useEffect(() => {
    let promiseToGetQuicklookPreview;
    if (!productImageURL) {
      const qp = findQuicklookPreviewFromProductImage(allQuicklookPreviews, productImage);
      if (!!qp) {
        setProductImageURL((qp && qp.url) || getDefaultProductImageURLBy(productImage));
      } else {
        promiseToGetQuicklookPreview = getQuicklookPreviewActionHandler(configurations, productImage)
          .then((newQuicklookPreview: QuicklookPreviewType) => {
            setProductImageURL((newQuicklookPreview && newQuicklookPreview.url) || getDefaultProductImageURLBy(productImage));
          })
      }
    }

    return () => {
      if (!!promiseToGetQuicklookPreview) {
        promiseToGetQuicklookPreview.cancel("cancel load preview");
      }
    };
    // eslint-disable-next-line
  }, []);

  const productImageId = getProductIdentifierFrom(productImage);
  return (
    <div key={`quicklook-preview-${productImageId}`}>
      <img
        className={classNames("quicklook-preview", className, {
          "d-none": !productImageURL
        })}
        src={productImageURL || ""}
        alt={`Quicklook Preview ${productImageId}`}
      />
      <img
        className={classNames("quicklook-preview isLoading", className, {
          "d-none": !!productImageURL
        })}
        src={loadingImageUrl}
        alt={`Quicklook Preview ${productImageId} is loading...`}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  configurations: state.application.configurations,
  searchFormValues: getSearchFormValuesByState(state),
  allQuicklookPreviews: state.geoMap.allQuicklookPreviews
});

const mapDispatchToState = (dispatch) => ({
  getQuicklookPreviewActionHandler: (configurations, productImage) => dispatch(getQuicklookPreviewDispatchAction(configurations, productImage))
});

export default connect(mapStateToProps, mapDispatchToState)(QuicklookPreviewWrapper);
